import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { SiteFooter } from '../components/site-footer';
import { SiteHeader } from '../components/site-header';
import { ApiError, fetchCatalog, type CatalogResponse } from '../lib/api';
import { usePageTitle } from '../lib/page-title';

/**
 * The ticker catalog with plan badges. Listing is plan-independent; the
 * server-side entitlement gate lives on the detail routes, so a Pro-only
 * ticker is browsable here and shows its upgrade prompt on its own page.
 */
export function CatalogPage() {
  usePageTitle('Tickers');
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  const catalog = useQuery<CatalogResponse>({
    queryKey: ['catalog', token],
    queryFn: () => fetchCatalog(token),
    enabled: Boolean(token),
  });

  const [search, setSearch] = useState('');
  const term = useDeferredValue(search).trim().toLowerCase();

  let body;
  if (catalog.isPending) {
    body = (
      <p className="section-state" role="status">
        Loading the catalog…
      </p>
    );
  } else if (catalog.isError) {
    if (catalog.error instanceof ApiError && catalog.error.status === 401) {
      body = (
        <p className="section-state state-error" role="alert">
          <span>Your session has expired.</span>
          <Link className="text-link" to="/signin">
            Sign in again
          </Link>
        </p>
      );
    } else {
      body = (
        <p className="section-state state-error" role="alert">
          <span>Could not load the catalog.</span>
          <button
            type="button"
            className="button-quiet button-inline"
            onClick={() => void catalog.refetch()}
          >
            Retry
          </button>
        </p>
      );
    }
  } else if (catalog.data.tickers.length === 0) {
    body = (
      <p className="section-state" role="status">
        No tickers are available yet.
      </p>
    );
  } else {
    const tickers = catalog.data.tickers;
    const matches = term
      ? tickers.filter(
          (ticker) =>
            ticker.symbol.toLowerCase().includes(term) || ticker.name.toLowerCase().includes(term),
        )
      : tickers;
    body = (
      <>
        <div className="catalog-tools">
          <label className="search">
            <span className="visually-hidden">Search tickers</span>
            <input
              type="search"
              placeholder="Search by symbol or company"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <p className="catalog-count" role="status">
            {term ? `${matches.length} of ${tickers.length} tickers` : `${tickers.length} tickers`}
          </p>
        </div>
        {matches.length === 0 ? (
          <p className="section-state">
            No ticker matches “{search.trim()}”. Try a symbol like AAPL or a company name.
          </p>
        ) : (
          <ul className="catalog-list">
            {matches.map((ticker) => (
              <li className="catalog-card" key={ticker.symbol}>
                <Link className="catalog-symbol" to={`/tickers/${ticker.symbol}`}>
                  {ticker.symbol}
                </Link>
                <span className="catalog-name">{ticker.name}</span>
                <span
                  className={
                    catalog.data.beta_mode || ticker.free_tier
                      ? 'plan-badge plan-badge-free'
                      : 'plan-badge'
                  }
                >
                  {catalog.data.beta_mode ? 'Free during beta' : ticker.free_tier ? 'Free' : 'Pro'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main className="page">
        <div className="page-head">
          <h1>Tickers</h1>
          <p className="lede">
            Every ticker links to its sentiment overview, 30-day trend, and latest scored headlines.
            {!catalog.data?.beta_mode &&
              ' Pro tickers unlock with the Pro plan from your account page.'}
          </p>
          {catalog.data?.beta_mode ? (
            <p className="notice" role="status">
              Beta access: every ticker is free during the beta period.
            </p>
          ) : null}
        </div>
        {body}
      </main>
      <SiteFooter />
    </>
  );
}
